import React, { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { consumeCode } from 'supertokens-auth-react/recipe/passwordless';

import CTAButton from '../components/CTAButton';
import ErrorMessage from '../components/ErrorMessage';
import ModalFooter from '../components/ModalFooter';
import RedirectToRoot from '../components/RedirectToRoot';
import TopNav from '../components/TopNav';
import UserTokenVerification from '../components/UserTokenVerification';
import { captureTokenVerification } from '../lib/utils/posthog';

interface TokenParams {
  type: string;
  contact: string;
  [key: string]: string;
}

export default function Token() {
  const { state } = useLocation();
  const { type, contact } = useParams<TokenParams>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>();

  // Redirect away if we don't have a valid redirectUrl
  if (!state?.redirectUrl) {
    return <RedirectToRoot />;
  }

  const handleChange = (token: string) => {
    setError(null);
  };

  const handleSubmit = async (token: string) => {
    setLoading(true);
    setError(null);

    try {
      const input = {
        userInputCode: token,
      };
      const resp = await consumeCode(input);
      setLoading(false);

      // Handle all possible return states
      switch (resp.status) {
        case 'OK':
          captureTokenVerification(type || '', contact || '', true);

          // Handle OK status
          // - add user id/email to trackers
          // - use createdNewRecipeUser for anything, new user notice?!
          //   i.e. redirect to ck property and add ?newUser=true
          // const { createdNewRecipeUser, user } = resp;
          // console.log(
          //   `User: ${user}, Created new recipe user: ${createdNewRecipeUser}`
          // );
          window.location.href = state.redirectUrl;
          break;

        case 'INCORRECT_USER_INPUT_CODE_ERROR':
        case 'EXPIRED_USER_INPUT_CODE_ERROR':
          const errorMessage =
            resp.status === 'EXPIRED_USER_INPUT_CODE_ERROR'
              ? 'Token is expired'
              : 'Invalid Token';
          captureTokenVerification(
            type || '',
            contact || '',
            false,
            errorMessage
          );

          // Handle code input fail state
          if (resp.status.startsWith('EXPIRED')) {
            setError('Token is expired');
          } else {
            setError('Invalid Token');
          }
          break;

        case 'RESTART_FLOW_ERROR':
          captureTokenVerification(
            type || '',
            contact || '',
            false,
            'Restart flow error'
          );

          // Handle restart flow error
          // - prolly needs to indicate that the user's token input opportunity has timedout
          // - redirect to /restart and inspect restart in the url params and show error?!
          setError('Invalid Token');
          break;

        case 'SIGN_IN_UP_NOT_ALLOWED':
          captureTokenVerification(
            type || '',
            contact || '',
            false,
            'Sign in/up not allowed'
          );

          // Handle sign in/up not allowed
          // - should never reach this
          break;
      }
    } catch (err: any) {
      setLoading(false);
      captureTokenVerification(type || '', contact || '', false);

      // General errors are user facing input errors
      if (err.isSuperTokensGeneralError === true) {
        setError(err.message);
      } else {
        window.alert('Oops! Something went wrong.');
      }
    }
  };

  return (
    <>
      <TopNav />
      <div
        role="main"
        className="min-h-full flex flex-col justify-center xxs:py-0 lg:py-4 sm:px-6 lg:px-8"
      >
        <div className="mt-8 xxs:mx-auto xxs:w-full xxs:max-w-lg">
          <div className="bg-white my-4 xxs:py-10 sm:py-16 lg:py-20 px-10 xxs:shadow xxs:rounded-lg sm:px-14 text-center">
            <h2 className="my-2 text-3xl font-avenir-black text-navy-blue">
              We just{' '}
              {type === 'phone'
                ? 'texted'
                : type === 'email'
                  ? 'emailed'
                  : 'contacted'}{' '}
              you
            </h2>
            <p className="my-2 text-base font-avenir-book text-opaque-navy">
              Please enter the verification code we sent to
              <span className="ml-1 text-navy-blue font-avenir-medium block">
                {contact}
              </span>
            </p>
            {!loading && error && (
              <ErrorMessage className="mt-6" message={error} />
            )}
            <div className="my-4 xxs:my-8">
              <UserTokenVerification
                disabled={loading}
                onChange={handleChange}
                onSubmit={handleSubmit}
              />
            </div>
            <div className="flex justify-between items-center w-full my-6">
              <p className="text-sm xxs:text-base text-navy-blue">
                Didn't receive the code?
              </p>
              <button className="text-xs xxs:text-sm font-avenir-medium rounded-full px-2 py-1 border border-primary-blue border-opacity-40 text-primary-blue hover:bg-primary-blue hover:border-opacity-5 hover:bg-opacity-20">
                <Link to="/login" className="mx-1.5">
                  Try Again
                </Link>
              </button>
            </div>
            <CTAButton
              ariaLabel="continue"
              text="Continue"
              icon={['far', 'lock']}
              loading={loading}
            />
            <ModalFooter className="text-opaque-navy text-xs mt-8" />
          </div>
        </div>
      </div>
    </>
  );
}
